'use client';

import React, { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared/Modal';
import { CrossBVModal } from 'assets/icons/CrossBVModal';
import { jetbrainsMono } from 'utils/fonts';
import CustomLink from 'components/shared/CustomLink';
import { SharedDetailModal } from '../../SharedDetailModal';
import modalStyles from '../../SharedDetailModal.module.css';
import learnMoreStyles from '../../DiagnosticLearnMoreModal.module.css';
import { PerformanceLabModalBodyScrollLock } from '../../PerformanceLabModalBodyScrollLock';
import type { PlanIncludeDetail } from '../../buy/india/types';
import { festiveCheckoutPath } from '../festiveOffer';
import type { FestiveCardItem } from '../useFestivePlanItems';
import pickerStyles from './useOpenFestivePlanDetail.module.css';

const includeLabel = (item: string | { label: string }) =>
  typeof item === 'string' ? item : item.label;

/** A content swap keeps the dialog mounted, so focus would drop to <body>. */
const focusAfterSwap = (selector: string) =>
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>(`[aria-modal="true"] ${selector}`)?.focus();
  });

type ServiceEntry = {
  label: string;
  detail: PlanIncludeDetail;
  icon?: string;
};

/**
 * Opens the same right-hand detail panel the pricing page uses.
 * A plan `detail` (cryotherapy, HBOT, sports massage, each IV) opens directly.
 * Recovery passes have no plan-level detail — Learn more lists the included
 * services, and each one opens that service's pricing detail.
 * The footer CTA opens the gift form for the card.
 */
export const useOpenFestivePlanDetail = () => {
  const modal = useModal();
  const tPlans = useTranslations('diagnostics');
  const tLanding = useTranslations('performance-lab');

  return useCallback(
    (item: FestiveCardItem, ctaLabel: string) => {
      const plan = item.detailPlan;
      const { name } = item;
      const detail = plan?.detail;
      const resolveText = (value: string) => {
        if (tPlans.has(value)) return tPlans(value);
        if (tLanding.has(value)) return tLanding(value);
        return value;
      };

      const services: ServiceEntry[] = (plan?.includes ?? []).flatMap((inc) => {
        if (typeof inc === 'string' || !inc.detail) return [];
        return [
          {
            label: inc.label,
            detail: inc.detail,
            icon: inc.detail.thumbnailUrl ?? inc.detail.imageUrl ?? plan?.thumbnailUrl,
          },
        ];
      });

      const close = () => {
        modal.hide();
        modal.clear();
      };

      const giftLink = (
        <CustomLink
          href={festiveCheckoutPath(item.id)}
          onClick={close}
          className={`${learnMoreStyles.modalFooterCta} ${jetbrainsMono.className}`}
        >
          {ctaLabel}
        </CustomLink>
      );

      const mount = (node: React.ReactNode) => {
        modal.set(<PerformanceLabModalBodyScrollLock>{node}</PerformanceLabModalBodyScrollLock>);
        modal.setFullView();
        modal.setCloseBtnHidden(true);
        modal.setTop();
        modal.setAlignRight();
        modal.setPortal(true);
        modal.show();
      };

      const openDetailModal = (
        title: string,
        entry: PlanIncludeDetail,
        icon?: string,
        onBack?: () => void,
      ) => {
        mount(
          <SharedDetailModal
            title={title}
            iconSrc={icon ?? entry.thumbnailUrl ?? plan?.thumbnailUrl}
            iconAlt=""
            showIconPlaceholder
            sections={entry.sections?.map((section) => ({
              title: section.title,
              content: section.content,
            }))}
            sectionImageSrc={entry.imageUrl ?? entry.thumbnailUrl}
            description={entry.description}
            benefits={entry.benefits}
            onClose={close}
            closeAriaLabel={tLanding('modal.closeAria')}
            resolveText={resolveText}
            footer={
              onBack ? (
                <div className={pickerStyles.footerStack}>
                  <button type="button" className={pickerStyles.back} onClick={onBack}>
                    {tLanding('festive.giftFlow.back')}
                  </button>
                  {giftLink}
                </div>
              ) : (
                giftLink
              )
            }
          />,
        );
      };

      const openServiceList = (returnTo?: number) => {
        mount(
          <div
            className={modalStyles.modalContainer}
            role="dialog"
            aria-modal="true"
            aria-label={name}
          >
            <button
              className={modalStyles.closeButton}
              type="button"
              onClick={close}
              aria-label={tLanding('modal.closeAria')}
            >
              <CrossBVModal />
            </button>
            <div className={modalStyles.headerSection}>
              <h2 className={modalStyles.headerTitle}>{name}</h2>
            </div>
            <div className={modalStyles.modalContent} data-lenis-prevent="true">
              <ul className={pickerStyles.list} aria-label={tLanding('festive.lineup.includesLabel')}>
                {services.map((service, index) => (
                  <li key={service.label}>
                    <button
                      type="button"
                      className={pickerStyles.row}
                      data-service-index={index}
                      onClick={() => {
                        openDetailModal(
                          resolveText(service.label),
                          service.detail,
                          service.icon,
                          () => openServiceList(index),
                        );
                        focusAfterSwap('button');
                      }}
                    >
                      <span>{resolveText(service.label)}</span>
                      <span className={pickerStyles.chevron} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className={modalStyles.footerWrapper}>
              <div className={modalStyles.gradientFade} />
              <div className={modalStyles.stickyFooter}>{giftLink}</div>
            </div>
          </div>,
        );
        if (returnTo !== undefined) {
          focusAfterSwap(`[data-service-index="${returnTo}"]`);
        }
      };

      if (detail) {
        openDetailModal(name, detail, detail.thumbnailUrl ?? plan?.thumbnailUrl);
        return;
      }

      if (services.length) {
        openServiceList();
        return;
      }

      const meta = [plan?.sessionInfo, plan?.validity]
        .filter((value): value is string => Boolean(value))
        .map(resolveText);
      const includes = (plan?.includes ?? [])
        .map((inc) => resolveText(includeLabel(inc)))
        .map((label) => label.charAt(0).toUpperCase() + label.slice(1));

      mount(
        <SharedDetailModal
          title={name}
          iconSrc={plan?.thumbnailUrl}
          iconAlt=""
          sections={[
            {
              content: [item.description, item.spec || meta.join(' · ')].filter(Boolean).join(' '),
            },
            ...(item.chips.length
              ? [{ title: tLanding('festive.lineup.chipsLabel'), content: item.chips.join(' · ') }]
              : []),
            ...(item.includes.length
              ? [{ title: tLanding('festive.lineup.includesLabel'), content: item.includes.join(' · ') }]
              : includes.length
                ? [
                    {
                      title: plan?.includesHeader ? resolveText(plan.includesHeader) : undefined,
                      content: includes.join(' · '),
                    },
                  ]
                : []),
          ]}
          onClose={close}
          closeAriaLabel={tLanding('modal.closeAria')}
          resolveText={resolveText}
          footer={giftLink}
        />,
      );
    },
    [modal, tPlans, tLanding],
  );
};
